import { FunctionComponent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { combineLatest } from 'rxjs'
import CheckIcon from '@mui/icons-material/Check'
import ClockIcon from '@mui/icons-material/AccessTime'
import Info from '@mui/icons-material/Info'
import { Loader2 } from 'lucide-react'
import classNames from 'classnames'
import Modal from 'components/shared/Modal'
import { AOL_DISPLAY_NAMES, PRACTICE_AREAS_DISPLAY_NAMES, PRACTISING_AOL_OTHER } from 'constants/aol/aol'
import { PRACTISING_AOL, PRIMARY_AOL, USER } from 'constants/requests'
import { updatePractisingAol, updatePrimaryAol } from 'services/SessionService'
import { refresh } from 'services/ApiService'
import { isValidResponse, mergeResponses } from 'libraries/front-end-core/helpers/dataflow'
import notificationToast from 'helpers/notificationToast'
import Dropdown from 'components/Dropdown'
import TextInput from 'components/TextInput'
import TealToolTip from 'components/TealToolTip'
import { AOLType } from 'types/AOL'
import { setPractisingAol, setPrimaryAol } from 'redux/actions/user'
import { AOL_TOOLTIP_TEXT } from 'helpers/aol/aolTooltipText'
import { getAolOptionsByRole, getPractisingAolDropdownOptions } from 'helpers/aol/aolHelpers'

const dropdownClasses = {
  chevron: 'black',
  menu: 'bg-white',
  option: ({ focus }: { focus: boolean }) => classNames('black no-underline', { 'bg-casper-white': focus, 'bg-white': !focus }),
  button: 'w-100 h2 bg-white ba b--bright-grey br2 justify-between'
}

interface AoLPreferenceModalProps {
  isOpen: boolean
  needsSetup: boolean
  setAoLPreferenceModalOpen: (isOpen: boolean) => void
  dismissAoLPreferenceModalForSession: () => void
}

const AoLPreferenceModal: FunctionComponent<AoLPreferenceModalProps> = ({
  isOpen,
  needsSetup,
  setAoLPreferenceModalOpen,
  dismissAoLPreferenceModalForSession
}) => {
  const dispatch = useDispatch()
  const userRoles = useSelector((state: any) => (state.user?.accessRoles as string[] | undefined) ?? [])
  const [primaryAol, setPrimaryAolState] = useState<AOLType | null>(null)
  const [practisingAol, setPractisingAolState] = useState<string | null>(null)
  const [customPractisingAol, setCustomPractisingAol] = useState('')
  const [saving, setSaving] = useState(false)

  const practisingAolDropdownOptions = getPractisingAolDropdownOptions(practisingAol)

  const primaryAolDropdownOptions = getAolOptionsByRole(userRoles).map(opt => ({
    label: opt.name,
    selected: primaryAol === opt.urlFragment,
    value: opt.urlFragment,
    disabled: opt.disabled,
    disabledReason: opt.disabledReason
  }))

  const canSave =
    primaryAol !== null &&
    practisingAol !== null &&
    practisingAol !== '' &&
    (practisingAol !== PRACTISING_AOL_OTHER || customPractisingAol.trim() !== '')

  const handleSave = (): void => {
    if (primaryAol === null || practisingAol === null || practisingAol === '') return
    const practisingAolValue = practisingAol === PRACTISING_AOL_OTHER ? customPractisingAol.trim() : practisingAol

    setSaving(true)
    const notificationId = notificationToast(
      <ClockIcon />,
      toast.TYPE.INFO,
      'Saving preferences'
    )

    // If saving fails (e.g. the api is unreachable) we don't want to block the user out
    // of the rest of the site behind a modal they can't otherwise dismiss - let them in
    // and ask again next time they log in instead, since primaryAolSetByUser stays false
    // server-side either way.
    const handleSaveFailure = (): void => {
      toast.dismiss(notificationId)
      notificationToast(
        <Info />,
        toast.TYPE.INFO,
        "We couldn't save your preferences right now. We'll ask you again next time you log in."
      )
      dismissAoLPreferenceModalForSession()
      setAoLPreferenceModalOpen(false)
      setSaving(false)
    }

    // updatePrimaryAol/updatePractisingAol are built on request() (front-end-core/helpers/api.js),
    // which translates every failure (network error, non-2xx, timeout) into a response value
    // rather than an RxJS error - so there's nothing for a catchError to ever catch here.
    combineLatest(
      [updatePrimaryAol(primaryAol), updatePractisingAol(practisingAolValue)],
      mergeResponses
    )
      .subscribe((response) => {
        if (!isValidResponse(response)) {
          handleSaveFailure()
          return
        }

        toast.dismiss(notificationId)
        notificationToast(
          <CheckIcon />,
          toast.TYPE.SUCCESS,
          'Preferences saved successfully',
          'bg-dark-sea-green'
        )
        dispatch(setPrimaryAol(primaryAol))
        dispatch(setPractisingAol(practisingAolValue))

        refresh(PRACTISING_AOL).subscribe()
        refresh(PRIMARY_AOL).subscribe()
        refresh(USER).subscribe(() => {
          setAoLPreferenceModalOpen(false)
          setSaving(false)
        })
      })
  }

  const open = isOpen || needsSetup
  const disableClose = needsSetup

  const handleClose = (): void => setAoLPreferenceModalOpen(false)

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title='FromCounsel is expanding its knowledge services!'
      size='medium'
      disableClose={disableClose}
      overflowVisible
      actions={
        <>
          {!disableClose && (
            <button
              type='button'
              onClick={handleClose}
              disabled={saving}
              className='pointer bg-transparent bn f5 black ph5 pv4 br3 hover-bg-ghost-white'
            >
              Cancel
            </button>
          )}
          <button
            type='button'
            onClick={handleSave}
            disabled={saving || !canSave}
            className='pointer bg-suez-canal br3 ph6 pv4 white f5 bn hover-o-90 flex items-center gap2'
          >
            {saving && <Loader2 size={16} className='spin-to-win' />}
            Save
          </button>
        </>
      }
    >
      <p className='mt0 mb6 lh-copy'>
        In order to provide users the best experience as our content grows, please select:
      </p>

      <div className='mb7'>
        <label className='mb2 f6 grey db'>
          Your primary FromCounsel subscription area<span className='crimson'>*</span>
          <TealToolTip title={AOL_TOOLTIP_TEXT.primary} placement='bottom' arrow={false} bgColor='dark'>
            <Info className='ml2 f5 fill-medium-bluish-grey hover-fill-light-slate-grey' />
          </TealToolTip>
        </label>
        <div className='w-100 relative'>
          <Dropdown
            align='left'
            options={primaryAolDropdownOptions}
            handleOnSelect={(val) => setPrimaryAolState(val as AOLType)}
            showSelectedIcons={false}
            label={(): JSX.Element => (
              <div className='w-100 truncate tl'>
                {primaryAol !== null
                  ? (AOL_DISPLAY_NAMES[primaryAol] !== '' ? AOL_DISPLAY_NAMES[primaryAol] : primaryAol)
                  : <span className='grey'>Select an option</span>}
              </div>
            )}
            classes={dropdownClasses}
          />
        </div>
      </div>

      <div className='mb7'>
        <label className='mb2 f6 grey db'>
          Your professional practice area<span className='crimson'>*</span>
          <TealToolTip title={AOL_TOOLTIP_TEXT.practising} placement='bottom' arrow={false} bgColor='dark'>
            <Info className='ml2 f5 fill-medium-bluish-grey hover-fill-light-slate-grey' />
          </TealToolTip>
        </label>
        <div className='w-100 relative'>
          <Dropdown
            align='left'
            options={practisingAolDropdownOptions}
            handleOnSelect={(val) => {
              setPractisingAolState(val as string)
              setCustomPractisingAol('')
            }}
            showSelectedIcons={false}
            label={(): JSX.Element => (
              <div className='w-100 truncate tl'>
                {practisingAol === PRACTISING_AOL_OTHER
                  ? 'Other'
                  : practisingAol !== null && practisingAol !== ''
                    ? (PRACTICE_AREAS_DISPLAY_NAMES[practisingAol] !== '' ? PRACTICE_AREAS_DISPLAY_NAMES[practisingAol] : practisingAol)
                    : <span className='grey'>Select an option</span>}
              </div>
            )}
            classes={dropdownClasses}
          />
        </div>
        {practisingAol === PRACTISING_AOL_OTHER && (
          <div className='mt4'>
            <TextInput
              placeholder='Enter your practice area'
              value={customPractisingAol}
              handleOnChange={setCustomPractisingAol}
            />
          </div>
        )}
      </div>

      <span className='f6'>Note all selections can be changed by going to My Account &gt; Preferences, so, eg, trainees can change this on rotation and qualification.</span>
    </Modal>
  )
}

export default AoLPreferenceModal
